import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Stat, EmptyState } from "../components/common";
import AppointmentDialog from "../components/AppointmentDialog";
import { itDate } from "../lib/constants";
import TaskDialog, { TASK_META } from "../components/TaskDialog";
import { TaskList } from "../components/TaskList";
import { Calendar, Camera, MapPin, Clock, AlertTriangle, PhoneCall, Package, FileText, Plus, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [apptId, setApptId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [dlg, setDlg] = useState({ open: false, kind: null, task: null });
  const nav = useNavigate();

  const loadTasks = async () => { try { const r = await api.get("/tasks?state=open"); setTasks(r.data); } catch {} };
  const load = async () => { const { data } = await api.get("/dashboard"); setData(data); if (data.role !== "montatore") loadTasks(); };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  useEffect(() => { if (data && data.role !== "montatore" && !clients.length) { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); } }, [data, clients.length]);

  if (!data) return <p className="text-muted-foreground animate-pulse">Caricamento…</p>;

  if (data.role === "montatore") {
    return (
      <div>
        <PageHeader title={`Ciao ${user?.name?.split(" ")[0]}`} subtitle={`I tuoi appuntamenti di oggi · ${itDate(data.today)}`} />
        {(!data.my_appointments || data.my_appointments.length === 0) ? (
          <EmptyState title="Nessun appuntamento oggi" hint="Buona giornata!" />
        ) : (
          <div className="space-y-3">
            {data.my_appointments.map((a) => (
              <button key={a.id} data-testid={`installer-appt-${a.id}`} onClick={() => setApptId(a.id)}
                className="w-full card-soft p-4 text-left lift flex items-center justify-between">
                <div>
                  <p className="font-head font-bold">{a.client_name}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><Clock size={14} /> {a.start_time} · {a.title}</p>
                  {a.address && <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin size={14} /> {a.address}</p>}
                </div>
                <div className="flex items-center gap-1 text-brand text-sm font-medium"><Camera size={18} /> {a.photo_count || 0}</div>
              </button>
            ))}
          </div>
        )}
        <AppointmentDialog appointmentId={apptId} open={!!apptId} onClose={() => setApptId(null)} onChanged={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Dashboard" subtitle={`Panoramica operativa · ${itDate(data.today)}`} />

      {data.pagamenti_scaduti?.length > 0 && (
        <div className="card-soft p-4 mb-5 border-l-4 border-destructive bg-red-50 flex items-start gap-3" data-testid="overdue-banner">
          <AlertTriangle className="text-destructive shrink-0" size={20} />
          <div>
            <p className="font-semibold text-destructive">Pagamenti scaduti ({data.pagamenti_scaduti.length})</p>
            <p className="text-sm text-muted-foreground">{data.pagamenti_scaduti.map((p) => `${p.client_name} (€${p.residual})`).join(" · ")}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Sopralluoghi da programmare" value={data.sopralluoghi_da_programmare} tone="brand" testid="stat-sopralluoghi" onClick={() => nav("/da-gestire/sopralluoghi")} />
        <Stat label="Preventivi da preparare" value={data.preventivi_da_preparare} testid="stat-preventivi" onClick={() => nav("/da-gestire/preventivi")} />
        <Stat label="Materiali da ordinare" value={(data.materiali_da_ordinare?.length || 0) + (data.materiali_task_aperti || 0)} testid="stat-materiali" onClick={() => nav("/da-gestire/materiali")} />
        <Stat label="Montaggi da programmare" value={data.montaggi_da_programmare} tone="green" testid="stat-montaggi" onClick={() => nav("/da-gestire/montaggi")} />
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mb-5">
        {[["richiamo", "Richiami di oggi", PhoneCall, "Nessun richiamo da fare."], ["materiale", "Materiali da ordinare", Package, "Nessun materiale in attesa."], ["preventivo", "Preventivi da preparare", FileText, "Nessun preventivo da preparare."]].map(([k, title, Icon, empty]) => (
          <div key={k} className="card-soft p-5 flex flex-col" data-testid={`dash-panel-${k}`}>
            <h3 className="font-head font-bold mb-3 flex items-center gap-2"><Icon size={18} className="text-brand" /> {title}</h3>
            <button data-testid={`dash-add-${k}`} onClick={() => setDlg({ open: true, kind: k, task: null })}
              className="w-full mb-3 flex items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-brand/50 text-brand font-semibold text-sm py-2.5 hover:bg-brand hover:text-white hover:border-brand transition-colors">
              <Plus size={16} /> {TASK_META[k].add}
            </button>
            <div className="flex-1">
              <TaskList kind={k} items={tasks.filter((t) => t.kind === k)} onOpen={(t) => setDlg({ open: true, kind: k, task: t })} onChanged={load} empty={empty} />
              {k === "materiale" && data.materiali_da_ordinare?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-1">Lavori confermati in attesa di materiale</p>
                  {data.materiali_da_ordinare.slice(0, 5).map((j) => (
                    <div key={j.id} data-testid={`dash-job-material-${j.id}`} className="text-sm py-1 cursor-pointer hover:text-brand" onClick={() => nav(`/lavori/${j.id}`)}>{j.product}</div>
                  ))}
                </div>
              )}
            </div>
            <button data-testid={`dash-all-${k}`} onClick={() => nav("/attivita")} className="mt-3 text-xs text-muted-foreground hover:text-brand flex items-center gap-1 self-start">Vedi tutti e storico <ArrowRight size={12} /></button>
          </div>
        ))}
      </div>

      <div className="grid gap-5">
        <div className="card-soft p-5">
          <h3 className="font-head font-bold mb-3 flex items-center gap-2"><Calendar size={18} className="text-brand" /> Lavori e appuntamenti di oggi</h3>
          {data.today_jobs?.length ? (
            <div className="space-y-2">
              {data.today_jobs.map((a) => (
                <button key={a.id} data-testid={`today-appt-${a.id}`} onClick={() => setApptId(a.id)} className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-accent transition text-left">
                  <div><p className="font-medium text-sm">{a.client_name}</p><p className="text-xs text-muted-foreground">{a.start_time} · {a.title}</p></div>
                  <span className="text-xs text-brand capitalize">{a.type}</span>
                </button>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">Nessun appuntamento oggi.</p>}
        </div>
      </div>

      <div className="card-soft p-5 mt-5">
        <h3 className="font-head font-bold mb-3">Prossimi appuntamenti</h3>
        {data.upcoming?.length ? (
          <div className="space-y-1">
            {data.upcoming.map((a) => (
              <button key={a.id} onClick={() => setApptId(a.id)} className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-accent text-left text-sm">
                <span className="font-medium">{a.client_name}</span>
                <span className="text-muted-foreground">{itDate(a.date)} · {a.start_time} · <span className="capitalize">{a.type}</span></span>
              </button>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground">Nessun appuntamento futuro.</p>}
      </div>

      <AppointmentDialog appointmentId={apptId} open={!!apptId} onClose={() => setApptId(null)} onChanged={load} />
      <TaskDialog open={dlg.open} kind={dlg.kind} task={dlg.task} clients={clients} onClose={() => setDlg({ open: false, kind: dlg.kind, task: null })} onChanged={load} />
    </div>
  );
}
